import { Clock } from 'lucide-react';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';

// Most of what the bill-validation screens display is not ours — MLF/DLF values,
// market status and network tariff rates all come from an upstream sync. Showing
// a number without saying when it landed asks the user to trust it blindly, so
// every panel backed by synced data carries one of these.

type Props = {
  /** ISO timestamp of the last update, or null when we've never synced it. */
  timestamp: string | null | undefined;
  /** Where the data came from, e.g. "Salesforce" or "MSATS". */
  source?: string;
  /** Shown when timestamp is null — defaults to "never synced". */
  emptyLabel?: string;
  className?: string;
};

const STALE_AFTER_DAYS = 90;

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function relative(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function isStale(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() > STALE_AFTER_DAYS * 86_400_000;
}

export const DataFreshness = ({ timestamp, source, emptyLabel, className }: Props) => {
  if (!timestamp) {
    return (
      <p className={cn('inline-flex items-center gap-1 text-xs text-gray-400', className)}>
        <Clock className="h-3 w-3" />
        {source ? `${source} — ` : ''}{emptyLabel ?? 'never synced'}
      </p>
    );
  }

  const stale = isStale(timestamp);

  return (
    <p
      className={cn('inline-flex items-center gap-1 text-xs', stale ? 'text-amber-600' : 'text-gray-400', className)}
      title={new Date(timestamp).toLocaleString('en-AU')}
    >
      <Clock className="h-3 w-3" />
      {source ? `${source} · ` : ''}updated {fmt(timestamp)} ({relative(timestamp)})
    </p>
  );
};
